import React, { useEffect, useRef, useState } from 'react';
import { Download, RefreshCw, Share2, X } from 'lucide-react';
import { BotMascot, MascotAvatar } from './Mascot';
import { svgToImage } from '../run/svgImage';
import { shareCard } from '../run/share';
import { cardFontsReady, cardToFile, drawResultCard } from '../share/drawResultCard';
import analytics from '../Services/analyticsService';

const OPTION_LABEL = { votedOut: 'Voted-out answer', podium: 'Podium' };
const LOGO = '/nameit-icon-512.png';

const loadImage = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

/**
 * The share button on a results screen. Tapping it draws the card and opens it in a
 * window with Share and Save, like NameIt Run, so the results screen stays compact.
 *
 * art: { key: { mascot, mood, letter } | { bot, state } } for every mascot the card draws.
 */
export default function ShareResultCard({ type, card, options = ['card'], getCopy, art, label, compact = false }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState(options[0]);
  const [pick, setPick] = useState(0);
  const [preview, setPreview] = useState(null);
  const [failed, setFailed] = useState(false);
  const [note, setNote] = useState('');
  const artRef = useRef(null);
  const images = useRef(null);
  const shown = useRef(null);
  const closeRef = useRef(null);
  const copy = getCopy(kind, pick);

  // The image on screen stays saveable until the next one replaces it.
  useEffect(() => () => shown.current && URL.revokeObjectURL(shown.current), []);

  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    // Share and Save wait for the card that matches the current choice and message.
    setPreview(null);
    (async () => {
      try {
        if (!images.current) {
          const entries = await Promise.all(
            Object.keys(art).map(async (key) => {
              const svg = artRef.current?.querySelector(`[data-art="${key}"] svg`);
              return [key, svg ? await svgToImage(new XMLSerializer().serializeToString(svg)) : null];
            })
          );
          images.current = { ...Object.fromEntries(entries), logo: await loadImage(LOGO) };
        }
        await cardFontsReady();
        const canvas = drawResultCard({ type, card, copy, kind, pick, art: images.current, label });
        const made = await cardToFile(canvas);
        if (!made) return;
        if (!live) {
          URL.revokeObjectURL(made.url);
          return;
        }
        if (shown.current) URL.revokeObjectURL(shown.current);
        shown.current = made.url;
        setPreview(made);
        setFailed(false);
      } catch (err) {
        console.warn('Result card failed to draw:', err);
        if (live) setFailed(true);
      }
    })();
    return () => {
      live = false;
    };
    // The card and art are fixed once the game is over; only the choice changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, kind, pick]);

  useEffect(() => {
    if (!open) return undefined;
    closeRef.current?.focus();
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const openCard = () => {
    setNote('');
    setOpen(true);
    analytics.track('result_card_opened', { type, card: kind });
  };

  const share = async () => {
    const outcome = await shareCard({ file: preview?.file, text: copy.message });
    analytics.track('result_card_shared', { type, card: kind, outcome });
    if (outcome === 'copied') setNote('Message copied. Save the image and paste the message with it.');
    else if (outcome === 'failed') setNote("Sharing isn't available here. Save the image instead.");
    else setNote('');
  };

  const save = () => {
    if (!preview?.url) return;
    const a = document.createElement('a');
    a.href = preview.url;
    a.download = 'nameit-results.png';
    document.body.appendChild(a);
    a.click();
    a.remove();
    analytics.track('result_card_shared', { type, card: kind, outcome: 'saved' });
  };

  const others = card.votedOut?.length || 0;

  return (
    <>
      <div ref={artRef} aria-hidden style={{ position: 'absolute', left: -9999, top: 0, width: 320, overflow: 'hidden' }}>
        {Object.entries(art).map(([key, a]) => (
          <div key={key} data-art={key}>
            {a.bot ? (
              <BotMascot bot={a.bot} state={a.state} size={300} />
            ) : (
              <MascotAvatar mascot={a.mascot} mood={a.mood} letter={a.letter} still size={300} shadow={false} />
            )}
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={openCard}
        className={
          compact
            ? "inline-flex items-center justify-center gap-1.5 h-11 px-5 rounded-full text-sm font-semibold text-red-700 bg-white border border-red-200 hover:bg-red-50 active:scale-95 transition-all"
            : "w-full inline-flex items-center justify-center gap-2 py-4 rounded-2xl font-bold text-lg text-red-700 bg-white border-2 border-red-200 hover:bg-red-50 shadow-sm active:scale-95 transition-all"
        }
      >
        <Share2 size={compact ? 16 : 20} aria-hidden />
        {getCopy(options[0], 0).button}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setOpen(false)}>
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Share your result"
            className="w-full max-w-sm flex flex-col items-center gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            {options.length > 1 && (
              <div className="flex rounded-full bg-white/95 p-1 shadow" role="group" aria-label="Card">
                {options.map((o) => (
                  <button
                    key={o}
                    type="button"
                    onClick={() => setKind(o)}
                    aria-pressed={kind === o}
                    className={`px-4 py-2 rounded-full text-sm font-bold transition ${kind === o ? 'bg-red-600 text-white shadow' : 'text-gray-600 hover:text-gray-900'}`}
                  >
                    {OPTION_LABEL[o]}
                  </button>
                ))}
              </div>
            )}

            <div className="w-full max-h-[60vh] aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl bg-orange-50 flex items-center justify-center">
              {preview ? (
                <img src={preview.url} alt={copy.message} className="w-full h-full object-contain" />
              ) : (
                <span className="text-sm font-semibold text-gray-500">{failed ? "The card couldn't be drawn." : 'Drawing your card…'}</span>
              )}
            </div>

            {kind === 'votedOut' && others > 1 && (
              <button type="button" onClick={() => setPick((p) => p + 1)} className="inline-flex items-center gap-1.5 text-sm font-bold text-white hover:text-red-100">
                <RefreshCw size={16} aria-hidden />
                Show another answer
              </button>
            )}

            <button
              type="button"
              onClick={share}
              disabled={!preview}
              className="w-full inline-flex items-center justify-center gap-2 py-4 rounded-2xl font-bold text-lg text-white bg-red-600 hover:bg-red-700 shadow-lg disabled:opacity-60 active:scale-95 transition-all"
            >
              <Share2 size={20} aria-hidden />
              {copy.button}
            </button>
            <div className="w-full flex gap-3">
              <button
                type="button"
                onClick={save}
                disabled={!preview}
                className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-2xl font-bold text-gray-800 bg-white hover:bg-gray-50 disabled:opacity-60"
              >
                <Download size={18} aria-hidden />
                Save image
              </button>
              <button
                ref={closeRef}
                type="button"
                onClick={() => setOpen(false)}
                className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-2xl font-bold text-gray-800 bg-white hover:bg-gray-50"
              >
                <X size={18} aria-hidden />
                Close
              </button>
            </div>
            {note && <p className="text-sm text-white text-center" role="status">{note}</p>}
          </div>
        </div>
      )}
    </>
  );
}
